import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CONSTANTS } from './data';
import { AudioManager } from './lib/AudioManager';
import TitleScreen from './components/ui/TitleScreen';
import InstructionsModal from './components/ui/InstructionsModal';
import PauseMenu from './components/ui/PauseMenu';
import FinalScreen from './components/ui/FinalScreen';
import Confetti from './components/ui/Confetti';
import GameUI from './components/game/GameUI';
import PlayerSelectionModal from './components/ui/PlayerSelectionModal';
import Card from './components/game/Card';
import useGameEngine from './hooks/useGameEngine';
import { PaletteProvider, usePalette } from './PaletteContext';

function AppContent() {
  const audioManager = React.useRef<AudioManager>(new AudioManager()).current;
  const {
    // Game state
    gameMode, showTitle, showInstructions, showPlayerSelection, deck, players, scores, currentPlayerIndex,
    drawnCard, isDrawing, isScoring, cardBeingPlaced, bustingPlayer, bustingCards, showConfetti,
    stealableStacks, showStealMessage, isGameOver, finalWinners, finalWinnerScore, speedLevel,
    isPaused, activePlayerPositions, playerNames, activePlayerPosition, isHumanTurn, bustAnimationTime,
    // Handlers
    handleSelectMode, handlePlayerSelectionComplete, handlePlayerSelectionBack, handleInstructionsComplete,
    handleRestart, handleDrawCardClick, handleStealClick, handlePause, handleToggleSpeed,
    handleEndTurn,
  } = useGameEngine(audioManager);

  const { palette } = usePalette();

  // Generate background cards with random properties (same as TitleScreen)
  const backgroundCards = useMemo(() => {
    const cards = [];
    const cardDistribution = CONSTANTS.CARD_DISTRIBUTION;
    const config = CONSTANTS.BACKGROUND_CARDS;
    for (let i = 0; i < config.COUNT; i++) {
      const randomCard = cardDistribution[Math.floor(Math.random() * cardDistribution.length)];
      const opacity = config.OPACITY_RANGE[0] + Math.random() * (config.OPACITY_RANGE[1] - config.OPACITY_RANGE[0]);
      const scale = config.SCALE_RANGE[0] + Math.random() * (config.SCALE_RANGE[1] - config.SCALE_RANGE[0]);
      const blur = config.BLUR_RANGE[0] + Math.random() * (config.BLUR_RANGE[1] - config.BLUR_RANGE[0]);
      const rotation = config.ROTATION_RANGE[0] + Math.random() * (config.ROTATION_RANGE[1] - config.ROTATION_RANGE[0]);
      cards.push({
        id: `bg-card-${i}`,
        card: {
          id: `bg-${i}`,
          color: randomCard.color,
          number: randomCard.number,
          imageAlias: randomCard.imageAlias
        },
        style: {
          position: 'absolute' as const,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          filter: `blur(${blur}px)`,
          zIndex: 1,
          pointerEvents: 'none' as const,
        },
        animationProps: {
          opacity,
          scale,
          rotation
        },
        animationDelay: Math.random() * 10
      });
    }
    return cards;
  }, []);

  // Set CSS variables for palette on root
  React.useEffect(() => {
    const root = document.documentElement;
    if (!palette) return;
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-primary-light', palette.primaryLight);
    root.style.setProperty('--color-primary-dark', palette.primaryDark);
    root.style.setProperty('--color-secondary', palette.secondary);
    root.style.setProperty('--color-accent', palette.accent);
    root.style.setProperty('--color-background', palette.background);
    root.style.setProperty('--color-background-light', palette.backgroundLight);
    root.style.setProperty('--color-text', palette.text);
    root.style.setProperty('--color-button', palette.button);
    root.style.setProperty('--color-button-text', palette.buttonText);
  }, [palette]);

  return (
    <div
      className="min-h-screen w-full relative flex items-center justify-center overflow-hidden font-sans text-white"
      style={{
        background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-light), var(--color-background))`
      }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-10 left-10 w-32 h-32 rounded-full blur-xl"
          style={{ background: 'var(--color-background-light, #166534)', opacity: 0.15 }}
        ></div>
        <div
          className="absolute top-1/3 right-20 w-48 h-48 rounded-full blur-2xl"
          style={{ background: 'var(--color-accent, #facc15)', opacity: 0.10 }}
        ></div>
        <div
          className="absolute bottom-20 left-1/4 w-40 h-40 rounded-full blur-xl"
          style={{ background: 'var(--color-secondary, #22c55e)', opacity: 0.10 }}
        ></div>
        <div
          className="absolute bottom-10 right-10 w-24 h-24 rounded-full blur-lg"
          style={{ background: 'var(--color-background-light, #166534)', opacity: 0.10 }}
        ></div>
      </div>

      {/* Background Cards (same as TitleScreen) */}
      {!showTitle && !showInstructions && !showPlayerSelection && (
        <div className="absolute inset-0 overflow-hidden">
          {backgroundCards.map((bgCard) => (
            <motion.div
              key={bgCard.id}
              style={bgCard.style}
              initial={{ 
                opacity: 0, 
                scale: 0.5,
                rotate: 0
              }}
              animate={{ 
                opacity: bgCard.animationProps.opacity,
                scale: bgCard.animationProps.scale,
                rotate: bgCard.animationProps.rotation,
                y: [0, -20, 0]
              }}
              transition={{ 
                duration: 2,
                delay: bgCard.animationDelay,
                y: {
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
            >
              <Card card={bgCard.card} />
            </motion.div>
          ))}
        </div>
      )}

      {/* TITLE SCREEN */}
      <AnimatePresence>
        {showTitle && (
          <TitleScreen onSelectMode={handleSelectMode} audioManager={audioManager} />
        )}
      </AnimatePresence>

      {/* PLAYER SELECTION MODAL */}
      <AnimatePresence>
        {showPlayerSelection && (
          <PlayerSelectionModal
            onSelect={handlePlayerSelectionComplete}
            onBack={handlePlayerSelectionBack}
            audioManager={audioManager}
          />
        )}
      </AnimatePresence>

      {/* INSTRUCTIONS MODAL */}
      <AnimatePresence>
        {showInstructions && (
          <InstructionsModal onClose={handleInstructionsComplete} audioManager={audioManager} />
        )}
      </AnimatePresence>
      
      {/* PAUSE MENU */}
      <AnimatePresence>
        {isPaused && (
          <PauseMenu
            onContinue={() => handlePause(false)}
            onMainMenu={handleRestart}
            audioManager={audioManager}
          />
        )}
      </AnimatePresence>

      {/* Hide the game UI when title or instructions are shown */}
      {!showTitle && !showInstructions && !showPlayerSelection && (
        <>
          <AnimatePresence>
            {showConfetti && <Confetti />}
          </AnimatePresence>

          <GameUI
            deck={deck}
            players={players}
            scores={scores}
            drawnCard={drawnCard}
            bustingPlayer={bustingPlayer}
            bustingCards={bustingCards}
            stealableStacks={stealableStacks}
            showStealMessage={showStealMessage}
            isGameOver={isGameOver}
            speedLevel={speedLevel}
            isHumanTurn={isHumanTurn}
            isDrawing={isDrawing}
            isScoring={isScoring}
            isPaused={isPaused}
            gameMode={gameMode}
            activePlayerPosition={activePlayerPosition}
            activePlayerPositions={activePlayerPositions}
            playerNames={playerNames}
            cardBeingPlaced={cardBeingPlaced}
            bustAnimationTime={bustAnimationTime}
            onDrawCard={handleDrawCardClick}
            onEndTurn={handleEndTurn}
            onSteal={handleStealClick}
            onPause={() => handlePause(true)}
            onToggleSpeed={handleToggleSpeed}
          />

          {/* FINAL SCREEN */}
          <AnimatePresence>
            {isGameOver && (
              <FinalScreen
                winnerNames={finalWinners}
                winnerScore={finalWinnerScore}
                scores={scores}
                onRestart={handleRestart}
                audioManager={audioManager}
                activePlayerPositions={activePlayerPositions}
                playerNames={playerNames}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <PaletteProvider>
      <AppContent />
    </PaletteProvider>
  );
}

export default App;
